import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";

function ActiveIpPanel({ events: scopedEvents = [], onRefresh }) {
  const events = Array.isArray(scopedEvents) ? scopedEvents : [];
  const [selectedIp, setSelectedIp] = useState(null);
  const loading = false;
  const error = "";

  const fetchEvents = async () => {
    if (typeof onRefresh === "function") {
      await onRefresh();
    }
  };

  const ipStats = useMemo(() => buildIpStats(events), [events]);

  const selectedIpData = selectedIp
    ? ipStats.find((item) => item.ip === selectedIp)
    : ipStats[0];

  useEffect(() => {
    if (!selectedIp && ipStats.length > 0) {
      setSelectedIp(ipStats[0].ip);
    }
  }, [ipStats, selectedIp]);

  if (loading) {
    return (
      <section style={styles.panel}>
        <h2 style={styles.title}>Active IP Intelligence</h2>
        <p style={styles.muted}>Loading active IP data...</p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <div style={styles.pill}>Congo Market AI • Active IP View</div>
          <h2 style={styles.title}>Active IP Intelligence</h2>
          <p style={styles.subtitle}>
            Click any remote IP to review ports, processes, severity,
            first-seen/last-seen activity, and related network events.
          </p>
        </div>

        <button style={styles.refreshButton} onClick={fetchEvents}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {ipStats.length === 0 ? (
        <div style={styles.emptyState}>
          No remote IP activity found yet. Run the network/firewall/login agents
          to populate this view.
        </div>
      ) : (
        <div style={styles.layout}>
          <div style={styles.ipList}>
            {ipStats.map((item) => (
              <button
                key={item.ip}
                style={{
                  ...styles.ipCard,
                  ...(selectedIp === item.ip ? styles.selectedIpCard : {}),
                }}
                onClick={() => setSelectedIp(item.ip)}
              >
                <div style={styles.ipTop}>
                  <strong>{item.ip}</strong>
                  <span style={getRiskBadge(item.high, item.medium)}>
                    {item.high > 0 ? "High" : item.medium > 0 ? "Medium" : "Low"}
                  </span>
                </div>

                <div style={styles.ipMetrics}>
                  <span>{item.count} event(s)</span>
                  <span>{item.ports.size} port(s)</span>
                  <span>{item.processes.size} process(es)</span>
                </div>

                <div style={styles.portChips}>
                  {Array.from(item.ports)
                    .slice(0, 5)
                    .map((port) => (
                      <span key={port} style={styles.portChip}>
                        {port}
                      </span>
                    ))}
                </div>
              </button>
            ))}
          </div>

          <div style={styles.detailPanel}>
            {selectedIpData ? (
              <IpDetails data={selectedIpData} />
            ) : (
              <div style={styles.emptyState}>Select an IP to view details.</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function IpDetails({ data }) {
  return (
    <div>
      <div style={styles.detailHeader}>
        <div>
          <span style={styles.detailLabel}>Selected IP</span>
          <h3 style={styles.detailTitle}>{data.ip}</h3>
        </div>

        <span style={getRiskBadge(data.high, data.medium)}>
          {data.high > 0 ? "High Risk" : data.medium > 0 ? "Medium Risk" : "Low Risk"}
        </span>
      </div>

      <div style={styles.detailMetricGrid}>
        <DetailMetric label="Total Events" value={data.count} />
        <DetailMetric label="High" value={data.high} />
        <DetailMetric label="Medium" value={data.medium} />
        <DetailMetric label="Low" value={data.low} />
      </div>

      <div style={styles.infoGrid}>
        <InfoBlock label="First Seen" value={formatDate(data.firstSeen)} />
        <InfoBlock label="Last Seen" value={formatDate(data.lastSeen)} />
        <InfoBlock label="Ports Touched" value={Array.from(data.ports).join(", ") || "N/A"} />
        <InfoBlock label="Processes" value={Array.from(data.processes).join(", ") || "N/A"} />
      </div>

      <div style={styles.relatedSection}>
        <h3 style={styles.sectionTitle}>Related Events</h3>

        {data.events.slice(0, 12).map((event) => (
          <div key={event.id} style={styles.eventRow}>
            <span style={getSeverityDot(event.severity)}></span>

            <div>
              <div style={styles.eventRowTop}>
                <strong>{event.event_type || "Network Event"}</strong>
                <span>{formatDate(event.created_at)}</span>
              </div>

              <p style={styles.eventRowMessage}>
                {event.remote_ip || "N/A"}:{event.remote_port || ""} →{" "}
                {event.local_ip || "N/A"}:{event.local_port || ""} •{" "}
                {event.process_name || "N/A"} • {event.status || "N/A"}
              </p>

              {event.message && <p style={styles.eventMessage}>{event.message}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailMetric({ label, value }) {
  return (
    <div style={styles.detailMetric}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div style={styles.infoBlock}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function buildIpStats(events) {
  const map = new Map();

  events.forEach((event) => {
    const ip = event.remote_ip;

    if (!ip || ip === "127.0.0.1" || ip === "::1") return;

    if (!map.has(ip)) {
      map.set(ip, {
        ip,
        count: 0,
        high: 0,
        medium: 0,
        low: 0,
        ports: new Set(),
        processes: new Set(),
        firstSeen: event.created_at,
        lastSeen: event.created_at,
        events: [],
      });
    }

    const item = map.get(ip);

    item.count += 1;
    item.events.push(event);

    if (event.severity === "High") {
      item.high += 1;
    } else if (event.severity === "Medium") {
      item.medium += 1;
    } else {
      item.low += 1;
    }

    if (event.remote_port) item.ports.add(String(event.remote_port));
    if (event.local_port) item.ports.add(String(event.local_port));
    if (event.process_name) item.processes.add(event.process_name);

    const eventTime = new Date(event.created_at).getTime();
    const firstTime = new Date(item.firstSeen).getTime();
    const lastTime = new Date(item.lastSeen).getTime();

    if (!Number.isNaN(eventTime)) {
      if (Number.isNaN(firstTime) || eventTime < firstTime) {
        item.firstSeen = event.created_at;
      }

      if (Number.isNaN(lastTime) || eventTime > lastTime) {
        item.lastSeen = event.created_at;
      }
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.high !== a.high) return b.high - a.high;
    if (b.medium !== a.medium) return b.medium - a.medium;
    return b.count - a.count;
  });
}

function getRiskBadge(high, medium) {
  if (high > 0) {
    return {
      ...styles.badge,
      background: "#fee2e2",
      color: "#991b1b",
    };
  }

  if (medium > 0) {
    return {
      ...styles.badge,
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    ...styles.badge,
    background: "#dcfce7",
    color: "#166534",
  };
}

function getSeverityDot(severity) {
  if (severity === "High") {
    return { ...styles.dot, background: "#ef4444" };
  }

  if (severity === "Medium") {
    return { ...styles.dot, background: "#f59e0b" };
  }

  return { ...styles.dot, background: "#22c55e" };
}

function formatDate(value) {
  if (!value) return "N/A";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const styles = {
  panel: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    padding: "22px",
    marginBottom: "18px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "18px",
    alignItems: "flex-start",
    marginBottom: "18px",
  },
  pill: {
    display: "inline-flex",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "#ede9fe",
    color: "#5b21b6",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "10px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    letterSpacing: "-0.04em",
    color: "#0f172a",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    maxWidth: "820px",
  },
  muted: {
    color: "#64748b",
  },
  refreshButton: {
    border: "0",
    background: "#0f172a",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "14px",
    fontWeight: "900",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "14px",
    padding: "12px 14px",
    marginBottom: "16px",
  },
  emptyState: {
    padding: "18px",
    borderRadius: "18px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    textAlign: "center",
    lineHeight: 1.6,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 0.8fr) minmax(0, 1.4fr)",
    gap: "16px",
  },
  ipList: {
    display: "grid",
    gap: "12px",
    maxHeight: "680px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  ipCard: {
    textAlign: "left",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: "18px",
    padding: "14px",
    cursor: "pointer",
  },
  selectedIpCard: {
    border: "2px solid #2563eb",
    background: "#eff6ff",
  },
  ipTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    alignItems: "center",
    marginBottom: "10px",
    color: "#0f172a",
    wordBreak: "break-word",
  },
  badge: {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "900",
    whiteSpace: "nowrap",
  },
  ipMetrics: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "800",
    marginBottom: "10px",
  },
  portChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  portChip: {
    display: "inline-flex",
    padding: "4px 8px",
    borderRadius: "999px",
    background: "#e2e8f0",
    color: "#334155",
    fontSize: "11px",
    fontWeight: "900",
  },
  detailPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    background: "#ffffff",
    padding: "18px",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "16px",
  },
  detailLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
  },
  detailTitle: {
    margin: "4px 0 0",
    color: "#0f172a",
    fontSize: "26px",
    wordBreak: "break-word",
  },
  detailMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "10px",
    marginBottom: "14px",
  },
  detailMetric: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "12px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
    marginBottom: "18px",
  },
  infoBlock: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "12px",
  },
  relatedSection: {
    marginTop: "10px",
  },
  sectionTitle: {
    margin: "0 0 10px",
    color: "#0f172a",
  },
  eventRow: {
    display: "grid",
    gridTemplateColumns: "10px 1fr",
    gap: "10px",
    padding: "12px 0",
    borderBottom: "1px solid #e2e8f0",
  },
  dot: {
    width: "9px",
    height: "9px",
    borderRadius: "999px",
    marginTop: "6px",
  },
  eventRowTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#0f172a",
    fontSize: "13px",
  },
  eventRowMessage: {
    margin: "4px 0",
    color: "#475569",
    fontSize: "13px",
  },
  eventMessage: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
};

export default ActiveIpPanel;